import { useState, useEffect } from "react";
import { X, UserPlus, Trash2, Users, Loader2, Save, KeyRound, CalendarClock, Plus, Clock, IdCard, Camera, ImageOff, ChevronDown, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError, dlToken } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const DOW = [["Pon", 0], ["Wt", 1], ["Śr", 2], ["Czw", 3], ["Pt", 4], ["Sob", 5], ["Nd", 6]];
const DEPTS = [
  { key: "instalatorzy", label: "Instalatorzy", role: "monter", field: true, color: "text-yellow-400" },
  { key: "serwis", label: "Serwis", role: "monter", field: true, color: "text-orange-400" },
  { key: "projektanci", label: "Projektanci", role: "projektant", color: "text-cyan-400" },
  { key: "infolinia", label: "Infolinia", role: "infolinia", color: "text-emerald-400" },
  { key: "ksiegowosc", label: "Księgowość", role: "odczyt", color: "text-slate-300" },
  { key: "admin", label: "Administracja", role: "admin", color: "text-pink-400" },
];
const ROLE_DEPT = { monter: "instalatorzy", projektant: "projektanci", infolinia: "infolinia", odczyt: "ksiegowosc", admin: "admin" };
const deptOf = (u) => u.department || ROLE_DEPT[u.role] || "ksiegowosc";
const inp = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-cyan-500";
const token = () => dlToken();

function MonterScheduleModal({ monter, onClose }) {
  const [days, setDays] = useState(() => new Set(monter.work_days || []));
  const [start, setStart] = useState(monter.work_start || "07:00");
  const [end, setEnd] = useState(monter.work_end || "15:00");
  const [absences, setAbsences] = useState([]);
  const [changed, setChanged] = useState(false);
  const [ab, setAb] = useState({ type: "urlop", from: "", to: "", reason: "" });
  const loadAbs = () => api.get(`/schedule/absences?user_id=${monter.id}`).then((r) => setAbsences(r.data || [])).catch(() => {});
  useEffect(() => { loadAbs(); /* eslint-disable-next-line */ }, []);
  const toggleDay = (d) => setDays((s) => { const n = new Set(s); n.has(d) ? n.delete(d) : n.add(d); return n; });
  const saveSchedule = async () => {
    try { await api.put(`/schedule/monters/${monter.id}/schedule`, { work_days: [...days], work_start: start, work_end: end }); toast.success("Zapisano grafik"); setChanged(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addAbsence = async () => {
    if (!ab.from || !ab.to) { toast.error("Podaj daty od i do"); return; }
    try { await api.post("/schedule/absences", { user_id: monter.id, ...ab }); toast.success("Dodano nieobecność"); setAb({ type: "urlop", from: "", to: "", reason: "" }); setChanged(true); loadAbs(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delAbsence = async (id) => { try { await api.delete(`/schedule/absences/${id}`); setChanged(true); loadAbs(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const AB_LABEL = { urlop: "Urlop", l4: "L4", inne: "Inne" };
  return (
    <div className="fixed inset-0 z-[900] bg-black/60 flex items-center justify-center p-4" onClick={() => onClose(changed)}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="monter-schedule-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 sticky top-0 bg-slate-900">
          <h3 className="font-heading font-bold flex items-center gap-2"><CalendarClock size={18} className="text-cyan-400" /> Grafik pracy: {monter.name}</h3>
          <button onClick={() => onClose(changed)} data-testid="sched-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[11px] text-slate-500">Grafik służy do przydzielania zadań, dostępności i śledzenia trasy w terenie. (Godziny logowania ustawiasz osobno przy koncie.)</p>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Dni pracy</label>
            <div className="flex flex-wrap gap-1.5">
              {DOW.map(([lbl, d]) => (
                <button key={d} onClick={() => toggleDay(d)} data-testid={`sched-day-${d}`}
                  className={`px-3 py-1.5 rounded text-sm border ${days.has(d) ? "bg-cyan-600 border-cyan-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-400 mb-1 block">Od godziny</label><input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="sched-start" className={inp} /></div>
            <div><label className="text-xs text-slate-400 mb-1 block">Do godziny</label><input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="sched-end" className={inp} /></div>
          </div>
          <button onClick={saveSchedule} data-testid="sched-save" className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2 text-sm flex items-center justify-center gap-2"><Save size={14} /> Zapisz grafik</button>
          <div className="border-t border-slate-800 pt-3">
            <label className="text-xs text-slate-400 mb-1.5 block">Nieobecności (urlop / L4 / inne)</label>
            <div className="space-y-1.5 mb-3">
              {absences.length === 0 && <div className="text-xs text-slate-500">Brak zaplanowanych nieobecności.</div>}
              {absences.map((a) => (
                <div key={a.id} className="flex items-center gap-2 text-sm bg-slate-800 border border-slate-700 rounded px-2.5 py-1.5" data-testid={`absence-${a.id}`}>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${a.type === "l4" ? "bg-red-500/20 text-red-300" : a.type === "urlop" ? "bg-cyan-500/20 text-cyan-300" : "bg-slate-500/20 text-slate-300"}`}>{AB_LABEL[a.type] || a.type}</span>
                  <span className="flex-1">{a.from} → {a.to}{a.reason ? ` · ${a.reason}` : ""}</span>
                  <button onClick={() => delAbsence(a.id)} data-testid={`absence-del-${a.id}`} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={ab.type} onChange={(e) => setAb({ ...ab, type: e.target.value })} data-testid="absence-type" className={inp}>
                <option value="urlop">Urlop</option><option value="l4">L4 (chorobowe)</option><option value="inne">Inne</option>
              </select>
              <input value={ab.reason} onChange={(e) => setAb({ ...ab, reason: e.target.value })} placeholder="Powód (opcjonalnie)" data-testid="absence-reason" className={inp} />
              <input type="date" value={ab.from} onChange={(e) => setAb({ ...ab, from: e.target.value })} data-testid="absence-from" className={inp} />
              <input type="date" value={ab.to} onChange={(e) => setAb({ ...ab, to: e.target.value })} data-testid="absence-to" className={inp} />
            </div>
            <button onClick={addAbsence} data-testid="absence-add" className="mt-2 w-full bg-amber-600 hover:bg-amber-500 text-black rounded py-2 text-sm flex items-center justify-center gap-2"><Plus size={14} /> Dodaj nieobecność</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoginHoursModal({ u, onClose }) {
  const [same, setSame] = useState(!!u.login_same_as_work);
  const [days, setDays] = useState(() => new Set(u.login_days || []));
  const [start, setStart] = useState(u.login_start || "");
  const [end, setEnd] = useState(u.login_end || "");
  const toggleDay = (d) => setDays((s) => { const n = new Set(s); n.has(d) ? n.delete(d) : n.add(d); return n; });
  const save = async () => {
    try {
      await api.put(`/users/${u.id}`, { login_same_as_work: same, login_days: [...days], login_start: start, login_end: end });
      toast.success("Zapisano godziny logowania"); onClose(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-[900] bg-black/60 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="login-hours-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold flex items-center gap-2"><Clock size={18} className="text-blue-400" /> Godziny logowania: {u.name}</h3>
          <button onClick={() => onClose(false)} data-testid="login-hours-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[11px] text-slate-500">Poza tymi godzinami pracownik NIE zaloguje się do programu. Puste = brak ograniczeń.</p>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="login-same-label">
            <input type="checkbox" checked={same} onChange={(e) => setSame(e.target.checked)} data-testid="login-same-toggle" className="w-4 h-4 accent-blue-500" />
            Takie same jak grafik pracy
          </label>
          {!same && (
            <>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Dni z dostępem</label>
                <div className="flex flex-wrap gap-1.5">
                  {DOW.map(([lbl, d]) => (
                    <button key={d} onClick={() => toggleDay(d)} data-testid={`login-day-${d}`}
                      className={`px-3 py-1.5 rounded text-sm border ${days.has(d) ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400"}`}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400 mb-1 block">Od godziny</label><input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="login-start" className={inp} /></div>
                <div><label className="text-xs text-slate-400 mb-1 block">Do godziny</label><input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="login-end" className={inp} /></div>
              </div>
            </>
          )}
          <button onClick={save} data-testid="login-hours-save" className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2 text-sm flex items-center justify-center gap-2"><Save size={14} /> Zapisz</button>
        </div>
      </div>
    </div>
  );
}

export default function UserManagementPanel({ onClose, currentUser }) {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", phone: "", department: "instalatorzy", is_monter: true, badge_validity_months: "" });
  const [busy, setBusy] = useState(false);
  const [schedUser, setSchedUser] = useState(null);
  const [loginUser, setLoginUser] = useState(null);
  const [openDept, setOpenDept] = useState("instalatorzy");
  const [bust, setBust] = useState(Date.now());

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const dept = DEPTS.find((d) => d.key === form.department) || DEPTS[0];
  const create = async () => {
    if (!form.email || !form.password || !form.name) return toast.error(t("Uzupełnij email, nazwę i hasło"));
    setBusy(true);
    const role = (dept.field && form.is_monter) ? "monter" : dept.role;
    try {
      await api.post("/users", { email: form.email, name: form.name, password: form.password, phone: form.phone, role, department: dept.key, is_monter: dept.field ? form.is_monter : false, badge_validity_months: form.badge_validity_months ? Number(form.badge_validity_months) : null });
      toast.success(t("Dodano pracownika"));
      setForm({ email: "", name: "", password: "", phone: "", department: form.department, is_monter: true });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (u) => {
    if (!window.confirm(`Usunąć pracownika ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success(t("Usunięto")); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const resetPassword = async (u) => {
    const p = window.prompt(`Nowe hasło dla ${u.email} (min. 6 znaków):`);
    if (p == null) return;
    if (p.length < 6) return toast.error(t("Hasło musi mieć min. 6 znaków"));
    try { await api.put(`/users/${u.id}`, { password: p }); toast.success(`${t("Zmieniono hasło dla")} ${u.email}`); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const uploadPhoto = async (u, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/users/${u.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Dodano zdjęcie"); setBust(Date.now()); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delPhoto = async (u) => {
    try { await api.delete(`/users/${u.id}/photo`); toast.success("Usunięto zdjęcie"); setBust(Date.now()); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleActive = async (u) => {
    const activate = u.active === false;
    let reason = "";
    if (!activate) {
      reason = window.prompt("Powód dezaktywacji konta (opcjonalnie — zapisze się w historii pracownika):", "");
      if (reason === null) return; // anulowano
    }
    try { await api.put(`/users/${u.id}`, { active: activate, deactivate_reason: reason }); toast.success(activate ? "Aktywowano konto" : "Dezaktywowano konto (blokada logowania + wylogowanie)"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="users-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2"><Users size={18} className="text-pink-400" /><h2 className="font-heading font-bold text-lg">Pracownicy</h2></div>
        <button onClick={onClose} data-testid="users-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-y-auto">
        {/* Add employee */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 h-fit">
          <h3 className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><UserPlus size={16} className="text-cyan-400" /> {t("Nowy pracownik")}</h3>
          <div className="space-y-2">
            <select value={form.department} onChange={(e) => { const d = e.target.value; setForm({ ...form, department: d, is_monter: d === "instalatorzy" }); }} data-testid="emp-department" className={inp}>
              {DEPTS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            {dept.field && (
              <label className="flex items-center gap-2 text-sm cursor-pointer text-slate-200" data-testid="emp-ismonter-label">
                <input type="checkbox" checked={form.is_monter} onChange={(e) => setForm({ ...form, is_monter: e.target.checked })} data-testid="emp-ismonter" className="w-4 h-4 accent-yellow-500" />
                Jest też monterem (aplikacja mobilna /m)
              </label>
            )}
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("Email")} data-testid="user-email" className={inp} />
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("Imię i nazwisko")} data-testid="user-name" className={inp} />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("Telefon")} data-testid="user-phone" className={inp} />
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t("Hasło")} type="text" data-testid="user-password" className={inp} />
            <input type="number" min="1" max="120" value={form.badge_validity_months} onChange={(e) => setForm({ ...form, badge_validity_months: e.target.value })} placeholder={t("Ważność identyfikatora (mies., puste = domyślne)")} data-testid="emp-badge-months" className={inp} />
            <button onClick={create} disabled={busy} data-testid="user-create" className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t("Utwórz konto")}
            </button>
            <p className="text-[11px] text-slate-500 mt-1">Grafik i godziny logowania ustawisz przyciskami przy pracowniku na liście. Zdjęcie i identyfikator również.</p>
          </div>
        </div>

        {/* Employee list grouped by department */}
        <div className="lg:col-span-2 space-y-2">
          {DEPTS.map((d) => {
            const list = users.filter((u) => deptOf(u) === d.key);
            if (list.length === 0 && d.key !== "instalatorzy") return null;
            const isOpen = openDept === d.key;
            return (
              <div key={d.key} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden" data-testid={`dept-${d.key}`}>
                <button onClick={() => setOpenDept(isOpen ? null : d.key)} data-testid={`dept-header-${d.key}`}
                  className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-slate-700/50">
                  <Users size={15} className={d.color} />
                  <span className="font-semibold text-sm flex-1 text-left">{d.label}</span>
                  <span className="text-xs text-slate-500">{list.length}</span>
                  <ChevronDown size={15} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-slate-700/60 divide-y divide-slate-800">
                    {list.length === 0 && <div className="text-xs text-slate-500 px-4 py-3">Brak pracowników w tym dziale.</div>}
                    {list.map((u) => (
                      <div key={u.id} className="flex items-center gap-3 px-3 py-2" data-testid={`user-row-${u.id}`}>
                        <div className="w-10 h-10 rounded-full bg-slate-900 border border-slate-700 overflow-hidden shrink-0 flex items-center justify-center">
                          {u.photo_path ? <img src={`${API}/users/${u.id}/photo?t=${bust}`} alt="" className="w-full h-full object-cover" data-testid={`user-photo-${u.id}`} /> : <Camera size={16} className="text-slate-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{u.name} {u.is_monter && d.key !== "instalatorzy" && <span className="text-[10px] text-yellow-400">· monter</span>}{u.active === false && <span className="text-[10px] text-red-400 ml-1" data-testid={`user-inactive-${u.id}`}>· nieaktywny</span>}</div>
                          <div className="text-[11px] text-slate-500 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                          {u.active === false && u.deactivate_reason && (
                            <div className="text-[11px] text-red-400/80 truncate" data-testid={`user-reason-${u.id}`} title={u.deactivate_reason}>{t("Powód")}: {u.deactivate_reason}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {(d.field || u.is_monter) && (
                            <button onClick={() => setSchedUser(u)} data-testid={`user-schedule-${u.id}`} title="Grafik i nieobecności" className="text-cyan-400 hover:text-cyan-300 p-1"><CalendarClock size={16} /></button>
                          )}
                          {u.role !== "admin" && (
                            <button onClick={() => setLoginUser(u)} data-testid={`user-loginhours-${u.id}`} title="Godziny logowania" className="text-blue-400 hover:text-blue-300 p-1"><Clock size={16} /></button>
                          )}
                          <label title="Wgraj zdjęcie" className="text-slate-300 hover:text-white p-1 cursor-pointer" data-testid={`user-photo-upload-${u.id}`}>
                            <Camera size={16} />
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(u, e.target.files[0])} />
                          </label>
                          {u.photo_path && <button onClick={() => delPhoto(u)} data-testid={`user-photo-del-${u.id}`} title="Usuń zdjęcie" className="text-slate-400 hover:text-red-300 p-1"><ImageOff size={16} /></button>}
                          <a href={`${API}/users/${u.id}/badge.pdf?auth=${encodeURIComponent(token())}`} target="_blank" rel="noreferrer" data-testid={`user-badge-${u.id}`} title="Drukuj identyfikator" className="text-emerald-400 hover:text-emerald-300 p-1"><IdCard size={16} /></a>
                          <button onClick={() => resetPassword(u)} data-testid={`user-reset-pwd-${u.id}`} title={t("Zmień hasło")} className="text-yellow-400 hover:text-yellow-300 p-1"><KeyRound size={16} /></button>
                          {u.id !== currentUser?.id && u.role !== "admin" && (
                            <button onClick={() => toggleActive(u)} data-testid={`user-active-${u.id}`} title={u.active === false ? "Aktywuj konto" : "Dezaktywuj konto"} className={`p-1 ${u.active === false ? "text-slate-500 hover:text-green-400" : "text-green-400 hover:text-slate-300"}`}>
                              {u.active === false ? <PowerOff size={16} /> : <Power size={16} />}
                            </button>
                          )}
                          {u.id !== currentUser?.id && <button onClick={() => del(u)} data-testid={`user-delete-${u.id}`} title={t("Usuń")} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={16} /></button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {schedUser && <MonterScheduleModal monter={schedUser} onClose={(c) => { setSchedUser(null); if (c) load(); }} />}
      {loginUser && <LoginHoursModal u={loginUser} onClose={(c) => { setLoginUser(null); if (c) load(); }} />}
    </div>
  );
}
